import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_USER_UNSEEN_EVENTS_RESET } from '@/api/endpoints';
import { USER_UNSEEN_EVENTS_QUERY_KEY } from '@/api/queryKeys';
import { useRefreshProfiles } from '@/hooks/useRefreshProfiles';
import { UnseenEventType } from '@/types/components';
import type { UnseenEventsResponse } from '@/types/user';
import { ErrorMessage, logRequestError } from '@/utils/errors';

const unseenEventCounters: Record<
  UnseenEventType,
  (unseenEvents: UnseenEventsResponse) => number
> = {
  [UnseenEventType.Evaluation]: ({ evaluationCount }) => evaluationCount,
  [UnseenEventType.BehaviorEntry]: ({ teacherMessagesCount }) =>
    teacherMessagesCount.behaviorEntries,
  [UnseenEventType.DiaryNote]: ({ teacherMessagesCount }) =>
    teacherMessagesCount.diaryAnnouncements,
  [UnseenEventType.NonAttendance]: ({ nonAttendanceCount }) =>
    nonAttendanceCount,
  [UnseenEventType.News]: ({ newsCount }) => newsCount.total,
};

export const useResetUnseenEvents = () => {
  const queryClient = useQueryClient();
  const refreshProfiles = useRefreshProfiles();

  const { mutateAsync: resetUnseenEvents } = useMutation({
    mutationFn: (eventTypes: UnseenEventType[]) =>
      apiClient.post(API_USER_UNSEEN_EVENTS_RESET, { eventTypes }),
  });

  return async (eventTypes: UnseenEventType[]) => {
    const unseenEvents = queryClient.getQueryData<UnseenEventsResponse>(
      USER_UNSEEN_EVENTS_QUERY_KEY,
    );

    if (!unseenEvents) {
      return;
    }

    const unseenCount = eventTypes.reduce(
      (sum, eventType) => sum + unseenEventCounters[eventType](unseenEvents),
      0,
    );

    if (unseenCount === 0) {
      return;
    }

    try {
      await resetUnseenEvents(eventTypes);
      await queryClient.invalidateQueries({
        queryKey: USER_UNSEEN_EVENTS_QUERY_KEY,
      });
      await refreshProfiles();
    } catch (error) {
      logRequestError(ErrorMessage.ResetUnseenEvents, error);
    }
  };
};
